import React from "react";
import { Star, Loader2, Eye, EyeOff } from "lucide-react";
import { money, monthly } from "@/lib/api";

export const Stars = ({ value = 0, size = 14, testId }: { value?: number; size?: number; testId?: string }) => (
  <span className="inline-flex gap-0.5" data-testid={testId} aria-label={`${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} strokeWidth={1.2} className={i <= Math.round(value) ? "fill-[var(--c-primary)] text-[var(--c-primary)]" : "text-neutral-300"} />
    ))}
  </span>
);

export const Spinner = ({ label = "Loading" }: { label?: string }) => (
  <div className="flex items-center justify-center gap-3 py-24 text-sm text-neutral-500" data-testid="loading-spinner">
    <Loader2 className="animate-spin" size={18} /> {label}
  </div>
);

export const PageHeader = ({ eyebrow, title, intro }: { eyebrow?: string; title: string; intro?: React.ReactNode }) => (
  <header className="px-5 md:px-12 pt-14 pb-10 max-w-[1400px] mx-auto">
    {eyebrow && <p className="eyebrow text-[var(--c-primary)] mb-4 rise">{eyebrow}</p>}
    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight rise rise-2" data-testid="page-title">{title}</h1>
    {intro && <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-neutral-600 rise rise-3" data-testid="page-intro">{intro}</p>}
  </header>
);

export const FinanceCue = ({ price, testId = "finance-cue" }: { price: number; testId?: string }) => (
  <p className="text-xs text-neutral-600" data-testid={testId}>
    or from <span className="font-medium text-neutral-900">{money(monthly(price))}/mo</span> with financing ·{" "}
    <a href="/financing" className="underline underline-offset-4 hover:text-[var(--c-primary)]">Learn more</a>
  </p>
);

const SAFE_HREF = /^(https?:\/\/|mailto:|tel:|\/(?![/\\])|#)/i;

const inline = (s: string) =>
  s.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|\[[^\]]+\]\([^)]+\))/g).map((t, i) => {
    if (t.startsWith("**") && t.endsWith("**")) return <strong key={i}>{t.slice(2, -2)}</strong>;
    if (t.length > 2 && t.startsWith("*") && t.endsWith("*")) return <em key={i}>{t.slice(1, -1)}</em>;
    const m = t.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!m) return t;
    const href = m[2].trim();
    return SAFE_HREF.test(href) ? <a key={i} href={href} className="underline underline-offset-4 hover:text-[var(--c-primary)]">{m[1]}</a> : m[1];
  });

const LIST_LINE = /^(- |\d+\. )/;
// Split headings and list runs into their own blocks so "## Title\n- item" renders as a real list
const normalise = (text: string) => {
  const out: string[] = [];
  for (const l of (text || "").split("\n")) {
    const prev = out[out.length - 1];
    if (prev && l && (prev.startsWith("## ") || LIST_LINE.test(prev) !== LIST_LINE.test(l))) out.push("");
    out.push(l);
  }
  return out.join("\n");
};

export function Markdown({ text }: { text: string }) {
  const parts = normalise(text).split(/\n\n+/);
  return (
    <div className="prose-lux">
      {parts.map((p, i) => {
        if (p.startsWith("## ")) {
          const [h, ...rest] = p.split("\n");
          return (
            <React.Fragment key={i}>
              <h2>{h.slice(3)}</h2>
              {rest.length > 0 && <p>{inline(rest.join(" "))}</p>}
            </React.Fragment>
          );
        }
        const img = p.trim().match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
        if (img && /^(https?:\/\/|\/)/.test(img[2])) return <figure key={i} className="my-10"><img src={img[2]} alt={img[1]} loading="lazy" className="w-full aspect-[3/2] object-cover bg-neutral-100" />{img[1].startsWith("Above") && <figcaption className="mt-3 text-xs text-neutral-500">{img[1]}</figcaption>}</figure>;
        const lines = p.split("\n");
        if (lines.length > 2 && lines.some((l) => /^\s*\|?\s*:?-{3,}\s*\|/.test(l))) {
          const rows = lines.filter((l) => !/^\s*\|?\s*:?-{2,}/.test(l)).map((l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()));
          return (
            <div key={i} className="my-6 overflow-x-auto"><table className="w-full text-sm border-collapse">
              <thead><tr>{rows[0].map((c, j) => <th key={j} className="text-left font-medium border-b border-neutral-300 px-3 py-2">{inline(c)}</th>)}</tr></thead>
              <tbody>{rows.slice(1).map((r, k) => <tr key={k}>{r.map((c, j) => <td key={j} className="border-b border-neutral-200 px-3 py-2 align-top">{inline(c)}</td>)}</tr>)}</tbody>
            </table></div>
          );
        }
        if (lines.every((l) => /^(- |\d+\. )/.test(l))) {
          const ordered = /^\d+\. /.test(lines[0]);
          const L = ordered ? "ol" : "ul";
          return <L key={i} className={`${ordered ? "list-decimal" : "list-disc"} pl-5 space-y-1.5 my-4`}>{lines.map((l, j) => <li key={j}>{inline(l.replace(/^(- |\d+\. )/, ""))}</li>)}</L>;
        }
        return <p key={i}>{inline(p)}</p>;
      })}
    </div>
  );
}

export const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="block">
    <span className="eyebrow text-neutral-500 block mb-2">{label}</span>
    {children}
  </label>
);

export const inputCls = "w-full bg-white border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-neutral-900 transition-colors";

export function PasswordInput({ testId, ...props }: any) {
  const [show, setShow] = React.useState(false);
  return (
    <div className="relative">
      <input {...props} type={show ? "text" : "password"} className={inputCls + " pr-12"} data-testid={testId} />
      <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} data-testid={`${testId}-toggle`}
        className="absolute inset-y-0 right-0 px-4 text-neutral-500 hover:text-neutral-900 transition-colors">
        {show ? <EyeOff size={17} strokeWidth={1.4} /> : <Eye size={17} strokeWidth={1.4} />}
      </button>
    </div>
  );
}
